import { NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { CommonButtonRowWidget, UtilityService } from "@eforall/common";
import { DialogService } from "@eforall/dashboard";
import { BusinessStageMap, LanguageId } from "@interfaces";
import { AppService, DomainDataService, TrimesterService } from "../../../../../services";
import { AccApplicationDialog } from "../../../acc-application-dialog";
import { PersonDialog } from "../../../person-dialog";
import { ReadonlyAccessWidget } from "../../../readonly-access/readonly-access.widget";
import { EntrepreneurProspectDialogDataService } from "../entrepreneur-prospect-dialog-data.service";


@Component({
	selector: 'entrepreneur-prospect-overview-dialog-part',
	standalone: true,
	imports: [
		ReadonlyAccessWidget,
		NgTemplateOutlet,
		CommonButtonRowWidget
	],
	templateUrl: './overview-content-dialog.part.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './overview-content-dialog.part.scss'
})
export class EntrepreneurProspectOverviewDialogPart {

	private appService = inject(AppService);
	private trimesterService = inject(TrimesterService);
	private dialogService = inject(DialogService);
	private domainData = inject(DomainDataService);
	private readonly util = inject(UtilityService);
	public readonly dialogDataService = inject(EntrepreneurProspectDialogDataService);

	public readonly entrepreneurProspectDetails = computed(() => {
		const details = this.dialogDataService.dialogData()?.details;
		if (!details) return undefined;

		const domainData = this.domainData.data();

		const selectedTrimester = this.trimesterService.selectedTrimester();

		return {
			...details,
			countAccApplications: this.dialogDataService.dialogData()?.applications.length ?? 0,
			accLead: {
				...details.accLead,
				language: details.accLead.languageId === LanguageId.Espanol ? 'Spanish' : 'English',
				industryName: domainData.industriesMap[details.accLead.industryId]?.name,
				businessStage: BusinessStageMap[details.accLead.businessStage],
				questionnaireLearn: domainData.questionnaireLearnMap[details.accLead.questionnaireLearnId]?.name ?? 'Not Available',
				startTrimesterName: this.appService.getTrimesterNameById(details.accLead.startTrimesterId),
				formattedCreatedUTC: this.formatUTC(details.accLead.createdUTC),
				formattedLastActivityUTC: this.formatUTC(details.accLead.lastActivityUTC),
			},
			accApplication: details.accApplication ? {
				...details.accApplication,
				accAppStatus: domainData.accApplicationStatusesMap[details.accApplication.accApplicationStatusId]?.name,
				trimesterSwitchoverStatus: details.accApplication.trimesterSwitchoverOldStatusId ? domainData.accApplicationStatusesMap[details.accApplication.trimesterSwitchoverOldStatusId]?.name : undefined,
				language: details.accApplication.languageId === LanguageId.Espanol ? 'Spanish' : 'English',
				industryName: domainData.industriesMap[details.accApplication.industryId]?.name,
				businessStage: BusinessStageMap[details.accApplication.businessStage],
				formattedCreatedUTC: this.formatUTC(details.accApplication.createdUTC),
				formattedFirstSubmissionUTC: this.formatUTC(details.accApplication.firstSubmissionUTC),
				formattedUpdatedUTC: this.formatUTC(details.accApplication.updatedUTC),
			} : undefined,
			currentTrimesterName: selectedTrimester?.name,
			prevTrimesterName: selectedTrimester?.prevTrimesterId ? this.appService.getTrimesterNameById(selectedTrimester.prevTrimesterId) : undefined,
		};
	});

	formatUTC(date: number | undefined): string | undefined {
		if (!date) return undefined;
		return this.util.date.formatUTC(date, 'MM/DD/YYYY', 'No Time', 'en-US');
	}

	public async markAsClosed() {

		const yes = await this.dialogService.confirm('Are you sure you want to mark this prospect as closed? This action cannot be undone.', 'Yes', 'No', [], 400, 200);

		const accLeadId = this.dialogDataService.dialogData()?.details?.accLead.accLeadId;

		if (yes && accLeadId) {
			await this.dialogDataService.markProspectAsClosed(true);
		}
	}

	public async markAsOpen() {

		const yes = await this.dialogService.confirm('Are you sure you want to mark this prospect as open? This action cannot be undone.', 'Yes', 'No', [], 400, 200);

		const accLeadId = this.dialogDataService.dialogData()?.details?.accLead.accLeadId;

		if (yes && accLeadId) {
			await this.dialogDataService.markProspectAsClosed(false);
		}
	}

	public openPersonDialog(personId: number) {
		this.dialogService.showCustom(PersonDialog, { data: { personId, toEdit: false } }, 800, 800);
	}

	public async openApplicationDetails() {
		const accApplicationId = this.dialogDataService.dialogData()?.details?.accApplication?.accApplicationId;
		if (!accApplicationId) return;
		await this.dialogService.showCustom(AccApplicationDialog, { data: { accApplicationId } }, 900, 900);
	}
}